import { useMutation, useQuery } from "@tanstack/react-query"

import { getEmailEnrichment } from "~/api/priorities"
import {
  checkEmailConnection,
  getSentEmails,
  getUser,
  getUserNotificationAccounts,
  updateUserNotificationAccounts
} from "~/api/user"
import useGlobalStore from "~/store/global"
import { EmailListType } from "~/types/engage"
import { ITokenInfo } from "~/types/global"

export function useGetUser(tokenInfo: ITokenInfo, mounted: boolean) {
  const setTokenInfo = useGlobalStore((store) => store.setTokenInfo)
  return useQuery({
    queryKey: ["user-info"],
    queryFn: async () => {
      setTokenInfo(tokenInfo)
      const res = await getUser(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo && mounted
  })
}

export function useCheckEmailConnection() {
  const tenantType = useGlobalStore((store) => store.tenant?.tenant_type)
  const isOSLite = tenantType?.includes("os_lite")
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["check-email-connection"],
    queryFn: async () => {
      const res = await checkEmailConnection(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo && !isOSLite
  })
}

export function useGetSentEmails(type = EmailListType.PLATFORM) {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["sent-emails", type],
    queryFn: async () => {
      const res = await getSentEmails(tokenInfo as ITokenInfo, type)
      return res.data
    },
    enabled: !!tokenInfo
  })
}

export function useGetEnrichEmailAddress(
  linkedin_url: string,
  enabled: boolean
) {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["get-email", linkedin_url],
    queryFn: async () => {
      const res = await getEmailEnrichment({
        tokenInfo: tokenInfo!,
        linkedin_url
      })
      return res.data
    },
    enabled: !!(tokenInfo && linkedin_url && enabled)
  })
}

export function useUserNotificationAccounts(enabled: boolean) {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: [
      "user-notification-accounts",
      tokenInfo?.user_id,
      tokenInfo?.tenant_id,
      linkedTenantId
    ],
    queryFn: async () => {
      const res = await getUserNotificationAccounts(tokenInfo!, linkedTenantId)
      const data = res?.data?.data?.result
      return data ?? null
    },
    enabled: !!(tokenInfo && enabled)
  })
}

export function useUpdateUserNotificationsAccounts() {
  const tokenInfo = useGlobalStore((s) => s.tokenInfo) as ITokenInfo
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useMutation({
    mutationFn: async ({
      notify_accounts,
      is_notification_disabled
    }: {
      notify_accounts: number[]
      is_notification_disabled: boolean
    }) => {
      const res = await updateUserNotificationAccounts(
        tokenInfo,
        linkedTenantId,
        {
          notify_accounts,
          is_notification_disabled
        }
      )
      return res.data
    }
  })
}
